import { compareStrings } from "@/utils/constants";

const COLOR_DETAIL_CODES = ["printingcolorsDetail", "printingcolorsDetailSide2"];
const LOAD_CODES = ["Colorloadpercentage", "ColorloadpercentageSide2"];
const LAYER_CODES = ["ColorLayers", "ColorLayersSide2"];

export { COLOR_DETAIL_CODES, LOAD_CODES, LAYER_CODES };

export const PRINTING_COLOR_DETAIL_FALLBACK_IDS = {
  color: [
    "8eff3238-a321-48f3-85eb-c14afaccbff3",
    "ae8d81d1-a0de-4967-b821-dae6937e7551",
  ],
  load: ["8b43efba-f28c-4e73-8fa0-cc64f3ea06f8"],
  layers: [
    "7a344769-6470-4584-8288-c7ff512af2ee",
    "54a29d4c-4fe1-4b3b-a4e8-cbfe99f656f5",
  ],
} as const;

export const getPrintingColorSideCodes = (isSide2 = false) => ({
  color: isSide2 ? "printingcolorsDetailSide2" : "printingcolorsDetail",
  load: isSide2 ? "ColorloadpercentageSide2" : "Colorloadpercentage",
  layers: isSide2 ? "ColorLayersSide2" : "ColorLayers",
  average: isSide2 ? "ColorsLoadAverageSide2" : "ColorsLoadAverage",
});

export const getMaterialColorIdsFromList = (list: any[] = []): string[] => {
  const colorsRoot = (list ?? []).find((m: any) =>
    compareStrings(String(m?.pathName ?? ""), "colors")
  );
  const walk = (nodes: any[]): string[] =>
    (nodes ?? []).flatMap((n) => {
      const ids = n?.valueId ? [n.valueId] : [];
      return [...ids, ...walk(n?.data ?? [])];
    });
  return walk(colorsRoot?.data ?? []);
};

export const clearPrintingColorDetailSelectionsInParameters = (
  parameters: any[],
  opts: {
    sectionId: string;
    subSectionId: string;
    actionIndex?: number;
    isSide2?: boolean;
  }
): any[] => {
  const codes = getPrintingColorSideCodes(opts.isSide2);
  const relatedCodes = [codes.color, codes.load, codes.layers];

  return parameters.map((p) => {
    const inScope =
      p.sectionId === opts.sectionId &&
      p.subSectionId === opts.subSectionId &&
      (typeof opts.actionIndex !== "number" || p.actionIndex === opts.actionIndex);

    if (inScope && relatedCodes.includes(p.parameterCode)) {
      return { ...p, values: [], valueIds: [] };
    }
    return p;
  });
};

export const mirrorColorLoadAverageToSettingParameters = (
  settingParams: any[],
  opts: {
    loadFillValue: any;
    colorParamCode: string;
    loadParamCode: string;
    avgParamCode: string;
    materialColorIds?: string[];
  }
): any[] => {
  const materialColorIds = opts.materialColorIds ?? [];
  const colorCatalogParam = settingParams.find(
    (p: any) => p.code === opts.colorParamCode
  );
  const loadCatalogParam = settingParams.find(
    (p: any) => p.code === opts.loadParamCode
  );

  const effectiveCatalogLen = Math.max(
    colorCatalogParam?.defaultValueArray?.length ?? 0,
    loadCatalogParam?.defaultValueArray?.length ?? 0,
    materialColorIds.length
  );

  if (effectiveCatalogLen === 0) {
    return settingParams;
  }

  const resolvedColorIds =
    colorCatalogParam?.defaultValueArray?.length > 0
      ? colorCatalogParam.defaultValueArray
      : materialColorIds.length > 0
        ? materialColorIds
        : [];

  const catalogLen = Math.max(resolvedColorIds.length, effectiveCatalogLen);

  return settingParams.map((param: any) => {
    if (param.code === opts.colorParamCode) {
      if (param.defaultValueArray?.length > 0) {
        return param;
      }
      if (resolvedColorIds.length > 0) {
        return {
          ...param,
          defaultValueArray: resolvedColorIds,
        };
      }
      return param;
    }
    if (param.code === opts.loadParamCode) {
      return {
        ...param,
        defaultValue: opts.loadFillValue,
        defaultValueArray: Array(catalogLen).fill(opts.loadFillValue),
      };
    }
    if (param.code === opts.avgParamCode) {
      return {
        ...param,
        defaultValue: opts.loadFillValue,
      };
    }
    return param;
  });
};

export const getCatalogLoadValueForColor = (
  settingParametersList: any[],
  colorValueId: string,
  isSide2 = false
): string | number | null => {
  const codes = getPrintingColorSideCodes(isSide2);
  const colorParam = findSettingParamByCodes(settingParametersList, [
    codes.color,
  ]);
  const loadParam = findSettingParamByCodes(settingParametersList, [
    codes.load,
  ]);
  if (!loadParam) return null;

  const colorIds =
    colorParam?.defaultValueArray?.length > 0
      ? colorParam.defaultValueArray
      : [];
  const index = colorIds.findIndex((id: string) => id === colorValueId);
  if (
    index !== -1 &&
    Array.isArray(loadParam.defaultValueArray) &&
    loadParam.defaultValueArray.length > index &&
    loadParam.defaultValueArray[index] != null
  ) {
    return loadParam.defaultValueArray[index];
  }
  if (loadParam.defaultValue != null && loadParam.defaultValue !== "") {
    return loadParam.defaultValue;
  }
  return null;
};

export const findColorDetailSettingParam = (
  settingParametersList: any[],
  parentParameterCode?: string
) => {
  const isSide2 =
    typeof parentParameterCode === "string" &&
    parentParameterCode.toLowerCase().includes("side2");
  return findSettingParamByCodes(
    settingParametersList,
    isSide2 ? ["printingcolorsDetailSide2"] : COLOR_DETAIL_CODES
  );
};

export const findRelatedColorSettingParams = (
  settingParametersList: any[],
  parentParameterCode?: string
) => {
  const codes = getPrintingColorSideCodes(
    typeof parentParameterCode === "string" &&
      parentParameterCode.toLowerCase().includes("side2")
  );
  return {
    colorParam: findSettingParamByCodes(settingParametersList, [codes.color]),
    loadParam: findSettingParamByCodes(settingParametersList, [codes.load]),
    layersParam: findSettingParamByCodes(settingParametersList, [codes.layers]),
  };
};

export const removeSingleColorSelectionAtIndex = (
  colorEntry: any,
  loadEntry: any,
  layersEntry: any,
  removeIndex: number
) => {
  const removeAt = (entry: any) => {
    if (!entry || removeIndex < 0) return;
    if (Array.isArray(entry.valueIds) && entry.valueIds.length > removeIndex) {
      entry.valueIds = entry.valueIds.filter((_, i) => i !== removeIndex);
    }
    if (Array.isArray(entry.values) && entry.values.length > removeIndex) {
      entry.values = entry.values.filter((_, i) => i !== removeIndex);
    }
  };

  removeAt(colorEntry);
  removeAt(loadEntry);
  removeAt(layersEntry);
};

export const findColorSelectionRemoveIndex = (
  colorEntry: any,
  colorValue: { valueId?: string; value?: string }
): number => {
  if (!colorEntry?.valueIds?.length) return -1;

  if (colorValue?.valueId) {
    const byId = colorEntry.valueIds.findIndex(
      (id: string) => id === colorValue.valueId
    );
    if (byId !== -1) return byId;
  }

  if (colorValue?.value && Array.isArray(colorEntry.values)) {
    return colorEntry.values.findIndex(
      (label: string) => label === colorValue.value
    );
  }

  return -1;
};

export const findSubProductParameterRow = (
  parameters: any[],
  opts: {
    parameterCode?: string;
    parameterId?: string;
    sectionId: string;
    subSectionId: string;
    actionIndex?: number;
  }
) => {
  if (!parameters?.length) return null;

  const inScope = (p: any) =>
    p.sectionId === opts.sectionId &&
    p.subSectionId === opts.subSectionId &&
    (typeof opts.actionIndex !== "number" || p.actionIndex === opts.actionIndex);

  const matches = parameters.filter((p) => {
    if (!inScope(p)) return false;
    if (opts.parameterCode && p.parameterCode === opts.parameterCode) return true;
    if (opts.parameterId && p.parameterId === opts.parameterId) return true;
    return false;
  });

  if (!matches.length) return null;

  return matches.reduce((best, current) => {
    const score = (row: any) =>
      Math.max(row?.valueIds?.length ?? 0, row?.values?.length ?? 0);
    return score(current) > score(best) ? current : best;
  });
};

export const findPrintingColorSubProductRow = (
  parameters: any[],
  opts: {
    role: "color" | "load" | "layers" | "average";
    sectionId: string;
    subSectionId: string;
    actionIndex?: number;
    isSide2?: boolean;
    settingParamsList?: any[];
  }
) => {
  const codes = getPrintingColorSideCodes(opts.isSide2);
  const codeByRole: Record<string, string> = {
    color: codes.color,
    load: codes.load,
    layers: codes.layers,
    average: codes.average,
  };
  const code = codeByRole[opts.role];
  const scope = {
    sectionId: opts.sectionId,
    subSectionId: opts.subSectionId,
    actionIndex: opts.actionIndex,
  };
  const paramDef = opts.settingParamsList?.find((p) => p.code === code);

  const byCode = findSubProductParameterRow(parameters, {
    parameterCode: code,
    parameterId: paramDef?.id,
    ...scope,
  });
  if (byCode) {
    return byCode;
  }

  const idsToTry = [
    ...(paramDef?.id ? [paramDef.id] : []),
    ...(opts.role !== "average"
      ? PRINTING_COLOR_DETAIL_FALLBACK_IDS[opts.role]
      : []),
  ];

  for (const parameterId of idsToTry) {
    const row = findSubProductParameterRow(parameters, {
      parameterId,
      ...scope,
    });
    if (row) {
      return row;
    }
  }

  return null;
};

export const getPrintingColorRowValueList = (entry: any): any[] => {
  const values = Array.isArray(entry?.values) ? entry.values : [];
  const valueIds = Array.isArray(entry?.valueIds) ? entry.valueIds : [];
  if (values.some((v) => v != null && v !== "")) {
    return values;
  }
  return valueIds;
};

export const hasCompleteSelectedColorValues = (
  colorEntry: any,
  valueEntry: any
): boolean => {
  const selectedCount = colorEntry?.valueIds?.length ?? 0;
  if (!selectedCount || !valueEntry) return false;
  const list = getPrintingColorRowValueList(valueEntry);
  return (
    list.length >= selectedCount &&
    list.slice(0, selectedCount).every((v) => v != null && v !== "")
  );
};

const mapSelectedValuesOntoCatalog = (
  catalogIds: string[],
  selectedIds: string[],
  selectedValues: any[],
  fallback: any,
  existingArray?: any[]
) => {
  const ids = catalogIds.length > 0 ? catalogIds : selectedIds;
  return ids.map((id, i) => {
    const selectedIndex = selectedIds.indexOf(id);
    if (
      selectedIndex !== -1 &&
      selectedValues[selectedIndex] != null &&
      selectedValues[selectedIndex] !== ""
    ) {
      return selectedValues[selectedIndex];
    }
    if (existingArray?.[i] != null && existingArray[i] !== "") {
      return existingArray[i];
    }
    return fallback;
  });
};

export const getSavedColorLoadAverageValue = (
  parameters: any[],
  opts: {
    sectionId: string;
    subSectionId: string;
    actionIndex?: number;
    isSide2?: boolean;
  }
): string | number | null => {
  const avgRow = findPrintingColorSubProductRow(parameters, {
    role: "average",
    sectionId: opts.sectionId,
    subSectionId: opts.subSectionId,
    actionIndex: opts.actionIndex,
    isSide2: opts.isSide2,
  });
  if (!avgRow) return null;
  const raw = avgRow.values?.[0] ?? avgRow.valueIds?.[0];
  return raw != null && raw !== "" ? raw : null;
};

export const hydratePrintingColorSettingParametersFromSubProduct = (
  settingParams: any[],
  subProductParameters: any[],
  opts: {
    sectionId: string;
    subSectionId: string;
    actionIndex?: number;
    isSide2?: boolean;
    materialColorIds?: string[];
  }
): any[] => {
  const codes = getPrintingColorSideCodes(opts.isSide2);
  const rowLookup = {
    sectionId: opts.sectionId,
    subSectionId: opts.subSectionId,
    actionIndex: opts.actionIndex,
    isSide2: opts.isSide2,
    settingParamsList: settingParams,
  };

  const colorEntry = findPrintingColorSubProductRow(subProductParameters, {
    role: "color",
    ...rowLookup,
  });
  const loadEntry = findPrintingColorSubProductRow(subProductParameters, {
    role: "load",
    ...rowLookup,
  });
  const layersEntry = findPrintingColorSubProductRow(subProductParameters, {
    role: "layers",
    ...rowLookup,
  });
  const avgValue = getSavedColorLoadAverageValue(subProductParameters, opts);

  const selectedIds: string[] = colorEntry?.valueIds ?? [];
  const selectedLoads = getPrintingColorRowValueList(loadEntry);
  const selectedLayers = getPrintingColorRowValueList(layersEntry);
  const hasSelectedValues =
    selectedIds.length > 0 &&
    (selectedLoads.length > 0 || selectedLayers.length > 0);

  if (avgValue == null && !hasSelectedValues) {
    return settingParams;
  }

  const colorParam = settingParams.find((p: any) => p.code === codes.color);
  const catalogColorIds: string[] =
    colorParam?.defaultValueArray?.length > 0
      ? colorParam.defaultValueArray
      : opts.materialColorIds ?? [];

  if (avgValue != null && !hasSelectedValues) {
    return mirrorColorLoadAverageToSettingParameters(settingParams, {
      loadFillValue: avgValue,
      colorParamCode: codes.color,
      loadParamCode: codes.load,
      avgParamCode: codes.average,
      materialColorIds: catalogColorIds,
    });
  }

  const loadFallback = avgValue ?? 100;
  const layerFallback = 1;

  return settingParams.map((param: any) => {
    if (param.code === codes.load) {
      const mapped = mapSelectedValuesOntoCatalog(
        catalogColorIds,
        selectedIds,
        selectedLoads,
        loadFallback,
        param.defaultValueArray
      );
      if (!mapped.length) {
        return param;
      }
      return {
        ...param,
        defaultValue: avgValue ?? param.defaultValue,
        defaultValueArray: mapped,
      };
    }
    if (param.code === codes.layers) {
      const mapped = mapSelectedValuesOntoCatalog(
        catalogColorIds,
        selectedIds,
        selectedLayers,
        param.defaultValue ?? layerFallback,
        param.defaultValueArray
      );
      if (!mapped.length) {
        return param;
      }
      return {
        ...param,
        defaultValueArray: mapped,
      };
    }
    if (param.code === codes.average && avgValue != null) {
      return {
        ...param,
        defaultValue: avgValue,
      };
    }
    if (
      param.code === codes.color &&
      !(param.defaultValueArray?.length > 0) &&
      catalogColorIds.length > 0
    ) {
      return {
        ...param,
        defaultValueArray: catalogColorIds,
      };
    }
    return param;
  });
};

export const mirrorColorLoadAverageInSubProductParameters = (
  parameters: any[],
  opts: {
    sectionId: string;
    subSectionId: string;
    actionIndex?: number;
    loadFillValue: any;
    isSide2?: boolean;
    perColorLoads?: Array<number | string>;
    settingParamsList?: any[];
  }
): any[] => {
  const codes = getPrintingColorSideCodes(opts.isSide2);
  const rowLookup = {
    sectionId: opts.sectionId,
    subSectionId: opts.subSectionId,
    actionIndex: opts.actionIndex,
    isSide2: opts.isSide2,
    settingParamsList: opts.settingParamsList,
  };

  const colorEntry = findPrintingColorSubProductRow(parameters, {
    role: "color",
    ...rowLookup,
  });
  if (!colorEntry?.valueIds?.length) {
    return parameters;
  }

  const loadEntry = findPrintingColorSubProductRow(parameters, {
    role: "load",
    ...rowLookup,
  });
  if (!loadEntry) {
    return parameters;
  }

  const loadIdx = parameters.indexOf(loadEntry);
  if (loadIdx === -1) {
    return parameters;
  }

  const hasPerColorLoads =
    Array.isArray(opts.perColorLoads) &&
    opts.perColorLoads.length === colorEntry.valueIds.length;

  const filledArr = hasPerColorLoads
    ? opts.perColorLoads!.map((v) => {
        const num = typeof v === "number" && !Number.isNaN(v) ? v : Number(v);
        return Number.isFinite(num) ? num : opts.loadFillValue;
      })
    : colorEntry.valueIds.map(() => opts.loadFillValue);

  const normalizedArr = filledArr.map((v) => String(v));
  const updated = [...parameters];
  updated[loadIdx] = {
    ...updated[loadIdx],
    parameterCode: codes.load,
    values: normalizedArr,
    valueIds: normalizedArr,
  };
  return updated;
};

const applyNormalizedPrintingColorRow = (
  parameters: any[],
  opts: {
    role: "load" | "layers";
    sectionId: string;
    subSectionId: string;
    actionIndex?: number;
    isSide2?: boolean;
    settingParamsList?: any[];
  }
): any[] => {
  const colorEntry = findPrintingColorSubProductRow(parameters, {
    role: "color",
    sectionId: opts.sectionId,
    subSectionId: opts.subSectionId,
    actionIndex: opts.actionIndex,
    isSide2: opts.isSide2,
    settingParamsList: opts.settingParamsList,
  });
  const valueEntry = findPrintingColorSubProductRow(parameters, {
    role: opts.role,
    sectionId: opts.sectionId,
    subSectionId: opts.subSectionId,
    actionIndex: opts.actionIndex,
    isSide2: opts.isSide2,
    settingParamsList: opts.settingParamsList,
  });
  if (!hasCompleteSelectedColorValues(colorEntry, valueEntry)) {
    return parameters;
  }

  const rowIdx = parameters.indexOf(valueEntry);
  if (rowIdx === -1) {
    return parameters;
  }

  const codes = getPrintingColorSideCodes(opts.isSide2);
  const selectedCount = colorEntry.valueIds.length;
  const normalized = getPrintingColorRowValueList(valueEntry)
    .slice(0, selectedCount)
    .map((v) => String(v));

  const updated = [...parameters];
  updated[rowIdx] = {
    ...updated[rowIdx],
    parameterCode: codes[opts.role],
    values: normalized,
    valueIds: normalized,
  };
  return updated;
};

export const syncPrintingColorLoadFromAverageInParameterList = (
  parameters: any[],
  settingParamsList?: any[]
): any[] => {
  if (!parameters?.length) {
    return parameters;
  }

  const groupKeys = new Set<string>();
  parameters.forEach((p) => {
    if (p?.sectionId && p?.subSectionId) {
      groupKeys.add(
        `${p.sectionId}|${p.subSectionId}|${p.actionIndex ?? 0}`
      );
    }
  });

  let updated = parameters;
  groupKeys.forEach((key) => {
    const [sectionId, subSectionId, actionIndexStr] = key.split("|");
    const actionIndex = Number(actionIndexStr);
    const scopeParams = updated.filter(
      (p) =>
        p.sectionId === sectionId &&
        p.subSectionId === subSectionId &&
        (p.actionIndex ?? 0) === actionIndex
    );

    for (const isSide2 of [false, true]) {
      const rowLookup = {
        sectionId,
        subSectionId,
        actionIndex,
        isSide2,
        settingParamsList,
      };
      const colorEntry = findPrintingColorSubProductRow(scopeParams, {
        role: "color",
        ...rowLookup,
      });
      const loadEntry = findPrintingColorSubProductRow(scopeParams, {
        role: "load",
        ...rowLookup,
      });

      if (hasCompleteSelectedColorValues(colorEntry, loadEntry)) {
        updated = applyNormalizedPrintingColorRow(updated, {
          role: "load",
          ...rowLookup,
        });
      } else {
        const avgValue = getSavedColorLoadAverageValue(scopeParams, {
          sectionId,
          subSectionId,
          actionIndex,
          isSide2,
        });
        if (avgValue != null && avgValue !== "") {
          updated = mirrorColorLoadAverageInSubProductParameters(updated, {
            sectionId,
            subSectionId,
            actionIndex,
            loadFillValue: avgValue,
            isSide2,
            settingParamsList,
          });
        }
      }

      updated = applyNormalizedPrintingColorRow(updated, {
        role: "layers",
        ...rowLookup,
      });
    }
  });

  return updated;
};

export const normalizeMaterialsList = (allMaterials: any): any[] => {
  if (Array.isArray(allMaterials)) return allMaterials;
  if (Array.isArray(allMaterials?.data)) return allMaterials.data;
  return [];
};

const findMaterialLabel = (
  allMaterials: any[] | undefined,
  valueId: string
): string => {
  const materials = normalizeMaterialsList(allMaterials);
  if (!materials.length || !valueId) return valueId;
  const walk = (nodes: any[]): string | null => {
    for (const node of nodes) {
      if (node?.valueId === valueId) return node.value ?? valueId;
      if (node?.data?.length) {
        const found = walk(node.data);
        if (found) return found;
      }
    }
    return null;
  };
  for (const material of materials) {
    if (material?.data?.length) {
      const found = walk(material.data);
      if (found) return found;
    }
  }
  return valueId;
};

export const buildSubProductValuesFromPreset = (
  param: any,
  selectedParam: any,
  allMaterials?: any[]
) => {
  const ids = selectedParam?.valueIds ?? [];
  if (COLOR_DETAIL_CODES.includes(param?.code)) {
    const labels = ids.map((id: string) =>
      findMaterialLabel(allMaterials, id)
    );
    return { values: labels, valueIds: ids };
  }
  return { values: [...ids], valueIds: [...ids] };
};

export const findSettingParamByCodes = (
  settingParameters: any[],
  codes: string[]
) => settingParameters?.find((p) => codes.includes(p?.code));

export const getActiveValuesConfig = (
  parameter: any,
  activeConfigId?: string | null
) => {
  const configs = parameter?.valuesConfigs?.filter(
    (c: any) => !c?.isHidden && !c?.isDeleted && c?.updateName
  );
  if (!configs?.length) return null;
  if (activeConfigId) {
    const match = configs.find((c: any) => c.id === activeConfigId);
    if (match) return match;
  }
  return configs.find((c: any) => c.isDefault) ?? configs[0];
};

export const ensureSelectedParameterValues = (
  valuesConfig: any,
  settingParametersList: any[],
  defaultCount = 1
) => {
  const printingcolorsDetail = findSettingParamByCodes(
    settingParametersList,
    COLOR_DETAIL_CODES
  );
  const loadParameter = findSettingParamByCodes(settingParametersList, LOAD_CODES);
  const layersParameter = findSettingParamByCodes(
    settingParametersList,
    LAYER_CODES
  );

  if (!printingcolorsDetail) return valuesConfig;

  const existing = valuesConfig?.selectedParameterValues ?? [];
  const colorEntry = existing.find(
    (p: any) => p.parameterId === printingcolorsDetail.id
  );
  const parsedFromName = parseColorCountFromUpdateName(
    valuesConfig?.updateName ?? ""
  );
  const count =
    colorEntry?.selectValuesCount ??
    parsedFromName ??
    (colorEntry?.valueIds?.length > 0 ? colorEntry.valueIds.length : defaultCount);

  const buildEntry = (paramId: string, withCount: boolean) => {
    const found = existing.find((p: any) => p.parameterId === paramId);
    if (found) {
      return {
        ...found,
        valueIds: found.valueIds ?? [],
        ...(withCount ? { selectValuesCount: found.selectValuesCount ?? count } : {}),
      };
    }
    return {
      parameterId: paramId,
      valueIds: [],
      ...(withCount ? { selectValuesCount: count } : {}),
    };
  };

  return {
    ...valuesConfig,
    selectedParameterValues: [
      buildEntry(printingcolorsDetail.id, true),
      ...(loadParameter ? [buildEntry(loadParameter.id, false)] : []),
      ...(layersParameter ? [buildEntry(layersParameter.id, false)] : []),
    ],
  };
};

export const syncDefaultValueArrayFromSelected = (
  settingParametersList: any[],
  selectedParameterValues: any[]
) => {
  const printingcolorsDetail = findSettingParamByCodes(
    settingParametersList,
    COLOR_DETAIL_CODES
  );
  const loadParameter = findSettingParamByCodes(settingParametersList, LOAD_CODES);
  const layersParameter = findSettingParamByCodes(
    settingParametersList,
    LAYER_CODES
  );
  if (!printingcolorsDetail) return settingParametersList;

  const colorSel = selectedParameterValues?.find(
    (p: any) => p.parameterId === printingcolorsDetail.id
  );
  const loadSel = loadParameter
    ? selectedParameterValues?.find((p: any) => p.parameterId === loadParameter.id)
    : null;
  const layersSel = layersParameter
    ? selectedParameterValues?.find(
        (p: any) => p.parameterId === layersParameter.id
      )
    : null;

  const allColorIds = printingcolorsDetail.defaultValueArray ?? [];
  const selectedIds = colorSel?.valueIds ?? [];

  return settingParametersList.map((param: any) => {
    if (COLOR_DETAIL_CODES.includes(param.code)) {
      return { ...param, defaultValueArray: selectedIds };
    }
    if (LOAD_CODES.includes(param.code) && loadSel?.valueIds) {
      const loads = selectedIds.map((id: string) => {
        const idx = allColorIds.indexOf(id);
        return idx !== -1 && loadSel.valueIds[idx] != null
          ? loadSel.valueIds[idx]
          : loadParameter?.defaultValue ?? 100;
      });
      return { ...param, defaultValueArray: loads };
    }
    if (LAYER_CODES.includes(param.code) && layersSel?.valueIds) {
      const layers = selectedIds.map((id: string) => {
        const idx = allColorIds.indexOf(id);
        return idx !== -1 && layersSel.valueIds[idx] != null
          ? layersSel.valueIds[idx]
          : layersParameter?.defaultValue ?? 1;
      });
      return { ...param, defaultValueArray: layers };
    }
    return param;
  });
};

export const isPrintingColorsParameter = (parameter: any) =>
  !!parameter?.setSettingIcon &&
  Array.isArray(parameter?.settingParameters) &&
  parameter.settingParameters.some((p: any) =>
    COLOR_DETAIL_CODES.includes(p?.code)
  );

export type ColorPresetTypeId = "black" | "cmyk" | "cmy" | "custom";

export type InkKey = "cyan" | "magenta" | "yellow" | "black";

const labelToInkKey = (label: string): InkKey | null => {
  const raw = label || "";
  const n = raw.toLowerCase();
  const p = /\(\s*([cmyk])\s*\)/i.exec(raw);
  if (p) {
    switch (p[1].toLowerCase()) {
      case "c":
        return "cyan";
      case "m":
        return "magenta";
      case "y":
        return "yellow";
      case "k":
        return "black";
      default:
        break;
    }
  }
  if (n.includes("cyan")) return "cyan";
  if (n.includes("magenta")) return "magenta";
  if (n.includes("yellow") && !n.includes("fluorescent")) return "yellow";
  if (n.includes("black")) return "black";
  return null;
};

export type ColorCatalogItem = { valueId: string; value: string };

const flattenColorMaterials = (allMaterials: any): ColorCatalogItem[] => {
  const materials = normalizeMaterialsList(allMaterials);
  const out: ColorCatalogItem[] = [];
  const walk = (nodes: any[]) => {
    for (const node of nodes ?? []) {
      if (node?.valueId && node?.value) {
        out.push({ valueId: node.valueId, value: node.value });
      }
      if (node?.data?.length) walk(node.data);
    }
  };
  const colorsRoot = materials.find((m) =>
    compareStrings(String(m?.pathName ?? ""), "colors")
  );
  if (colorsRoot?.data?.length) {
    walk(colorsRoot.data);
  }
  return out;
};

export const getColorCatalogFromMaterials = (
  allMaterials: any = []
): ColorCatalogItem[] => flattenColorMaterials(allMaterials);

export const getSelectedColorCatalogItems = (
  valuesConfig: any,
  settingParametersList: any[],
  allMaterials: any[] = []
): ColorCatalogItem[] => {
  const printingcolorsDetail = findSettingParamByCodes(
    settingParametersList,
    COLOR_DETAIL_CODES
  );
  if (!printingcolorsDetail) return [];
  const colorEntry = valuesConfig?.selectedParameterValues?.find(
    (p: any) => p.parameterId === printingcolorsDetail.id
  );
  const valueIds: string[] = colorEntry?.valueIds ?? [];
  if (!valueIds.length) return [];
  const catalog = getColorCatalogFromMaterials(allMaterials);
  return valueIds
    .map((id) => catalog.find((item) => item.valueId === id))
    .filter(Boolean) as ColorCatalogItem[];
};

export const findMaterialIdsByInkKeys = (
  allMaterials: any,
  inkKeys: InkKey[]
): string[] => {
  const catalog = flattenColorMaterials(allMaterials);
  const ids: string[] = [];
  for (const key of inkKeys) {
    const match = catalog.find((item) => labelToInkKey(item.value) === key);
    if (match?.valueId) ids.push(match.valueId);
  }
  return ids;
};

export const COLOR_PRESET_TYPE_OPTIONS: {
  id: ColorPresetTypeId;
  label: string;
  fixedCount: number | null;
  inkKeys: InkKey[] | null;
  defaultUpdateName: string;
}[] = [
  {
    id: "black",
    label: "Black",
    fixedCount: 1,
    inkKeys: ["black"],
    defaultUpdateName: "Black",
  },
  {
    id: "cmyk",
    label: "Full Color | 4 Colors Cmyk",
    fixedCount: 4,
    inkKeys: ["cyan", "magenta", "yellow", "black"],
    defaultUpdateName: "Full Color | 4 Colors Cmyk",
  },
  {
    id: "cmy",
    label: "Epm | 3 Colors Cmy",
    fixedCount: 3,
    inkKeys: ["cyan", "magenta", "yellow"],
    defaultUpdateName: "Epm | 3 Colors Cmy",
  },
  {
    id: "custom",
    label: "Custom",
    fixedCount: null,
    inkKeys: null,
    defaultUpdateName: "",
  },
];

export const buildColorCountOptions = (max = 7) =>
  Array.from({ length: max }, (_, i) => {
    const n = i + 1;
    return {
      id: n,
      label: n === 1 ? "1 Color" : `${n} Colors`,
    };
  });

const normalizePresetName = (value: string) =>
  (value ?? "").replace(/\s/g, "").toLowerCase();

/** Parse "1 Color", "2 Colors", … "9 Colors" style labels into a numeric count. */
export const parseColorCountFromUpdateName = (
  updateName: string
): number | null => {
  const match = /^\s*(\d+)\s*colors?\s*$/i.exec((updateName ?? "").trim());
  if (!match) return null;
  const count = parseInt(match[1], 10);
  return count >= 1 && count <= 9 ? count : null;
};

export const isCountOnlyColorPreset = (valueConfig: any): boolean =>
  parseColorCountFromUpdateName(valueConfig?.updateName ?? "") != null;

export type PrintingColorValidationResult = {
  valid: boolean;
  messageKey?: string;
  messageParams?: Record<string, unknown>;
};

const resolveColorConfigEntry = (
  valueConfig: any,
  settingParametersList: any[]
) => {
  const printingcolorsDetail = findSettingParamByCodes(
    settingParametersList,
    COLOR_DETAIL_CODES
  );
  if (!printingcolorsDetail || !valueConfig) return null;

  const parsedFromName = parseColorCountFromUpdateName(
    valueConfig.updateName ?? ""
  );
  const colorEntry = valueConfig.selectedParameterValues?.find(
    (p: any) => p.parameterId === printingcolorsDetail.id
  );

  if (!colorEntry) {
    if (parsedFromName == null) return null;
    return {
      parameterId: printingcolorsDetail.id,
      valueIds: [] as string[],
      selectValuesCount: parsedFromName,
    };
  }

  if (colorEntry.selectValuesCount == null && parsedFromName != null) {
    return { ...colorEntry, selectValuesCount: parsedFromName };
  }

  return colorEntry;
};

/** Match row display name to a built-in preset (Black / CMYK / CMY). */
export const inferPresetTypeFromUpdateName = (
  updateName: string
): ColorPresetTypeId | null => {
  const normalized = normalizePresetName(updateName);
  if (!normalized) return null;

  for (const preset of COLOR_PRESET_TYPE_OPTIONS) {
    if (preset.id === "custom") continue;
    if (
      normalized === normalizePresetName(preset.label) ||
      normalized === normalizePresetName(preset.defaultUpdateName)
    ) {
      return preset.id;
    }
  }
  return null;
};

export const inferPresetTypeFromConfig = (
  valuesConfig: any,
  settingParametersList: any[],
  allMaterials: any[] = []
): ColorPresetTypeId => {
  const fromUpdateName = inferPresetTypeFromUpdateName(
    valuesConfig?.updateName ?? ""
  );
  if (fromUpdateName) return fromUpdateName;

  const printingcolorsDetail = findSettingParamByCodes(
    settingParametersList,
    COLOR_DETAIL_CODES
  );
  if (!printingcolorsDetail) return "custom";
  const colorEntry = valuesConfig?.selectedParameterValues?.find(
    (p: any) => p.parameterId === printingcolorsDetail.id
  );
  const valueIds: string[] = colorEntry?.valueIds ?? [];
  if (valueIds.length === 0) return "custom";

  const matches = (inkKeys: InkKey[]) => {
    const expected = findMaterialIdsByInkKeys(allMaterials, inkKeys);
    if (expected.length !== inkKeys.length) return false;
    return (
      expected.length === valueIds.length &&
      expected.every((id, i) => id === valueIds[i])
    );
  };

  if (matches(["black"]) && valueIds.length === 1) return "black";
  if (matches(["cyan", "magenta", "yellow", "black"])) return "cmyk";
  if (matches(["cyan", "magenta", "yellow"])) return "cmy";
  return "custom";
};

const syncLoadAndLayerIds = (
  selectedParameterValues: any[],
  colorIds: string[],
  settingParametersList: any[],
  allColorIds: string[]
) => {
  const loadParam = findSettingParamByCodes(settingParametersList, LOAD_CODES);
  const layersParam = findSettingParamByCodes(
    settingParametersList,
    LAYER_CODES
  );
  const loadEntry = loadParam
    ? selectedParameterValues.find((p) => p.parameterId === loadParam.id)
    : null;
  const layersEntry = layersParam
    ? selectedParameterValues.find((p) => p.parameterId === layersParam.id)
    : null;

  const mapDefaults = (defaultArray: any[], fallback: any) =>
    colorIds.map((id) => {
      const idx = allColorIds.indexOf(id);
      return idx !== -1 && defaultArray?.[idx] != null
        ? defaultArray[idx]
        : fallback;
    });

  if (loadEntry) {
    loadEntry.valueIds = mapDefaults(
      loadParam?.defaultValueArray ?? [],
      loadParam?.defaultValue ?? 100
    );
  }
  if (layersEntry) {
    layersEntry.valueIds = mapDefaults(
      layersParam?.defaultValueArray ?? [],
      layersParam?.defaultValue ?? 1
    );
  }
};

export const applyColorPresetSelection = (
  valuesConfig: any,
  presetTypeId: ColorPresetTypeId,
  colorCount: number,
  settingParametersList: any[],
  allMaterials: any[],
  customColorIds?: string[]
) => {
  const preset = COLOR_PRESET_TYPE_OPTIONS.find((p) => p.id === presetTypeId);
  const ensured = ensureSelectedParameterValues(
    valuesConfig,
    settingParametersList,
    colorCount
  );
  const printingcolorsDetail = findSettingParamByCodes(
    settingParametersList,
    COLOR_DETAIL_CODES
  );
  if (!printingcolorsDetail) return ensured;

  const allColorIds: string[] = printingcolorsDetail.defaultValueArray?.length
    ? printingcolorsDetail.defaultValueArray
    : findMaterialIdsByInkKeys(allMaterials, [
        "cyan",
        "magenta",
        "yellow",
        "black",
      ]);

  let colorIds: string[] = [];
  let count: number;
  const targetCount = Math.max(1, colorCount);

  if (presetTypeId === "custom") {
    count = targetCount;
    colorIds = customColorIds?.length
      ? customColorIds.slice(0, targetCount)
      : [];
  } else if (preset?.inkKeys?.length) {
    colorIds = findMaterialIdsByInkKeys(allMaterials, preset.inkKeys);
    count = preset.fixedCount ?? colorIds.length;
  } else {
    count =
      preset?.fixedCount != null ? preset.fixedCount : targetCount;
  }

  const selectedParameterValues = (ensured.selectedParameterValues ?? []).map(
    (entry: any) => {
      if (entry.parameterId !== printingcolorsDetail.id) return entry;
      return {
        ...entry,
        selectValuesCount: count,
        valueIds: [...colorIds],
      };
    }
  );

  syncLoadAndLayerIds(
    selectedParameterValues,
    colorIds,
    settingParametersList,
    allColorIds
  );

  let updateName = valuesConfig?.updateName ?? "";
  if (presetTypeId === "custom") {
    // Keep user-provided name; do not auto-fill generic "N Colors" labels
  } else if (preset?.defaultUpdateName) {
    updateName = preset.defaultUpdateName;
  }

  return {
    ...ensured,
    updateName,
    colorPresetType: presetTypeId,
    colorPresetCount: count,
    selectedParameterValues,
  };
};

export const getColorCountFromConfig = (
  valuesConfig: any,
  settingParametersList: any[]
): number => {
  const colorEntry = resolveColorConfigEntry(
    valuesConfig,
    settingParametersList
  );
  return (
    colorEntry?.selectValuesCount ??
    (colorEntry?.valueIds?.length > 0 ? colorEntry.valueIds.length : 1)
  );
};

/** When the option defines a complete fixed ink set, returns their material ids (locked selection). */
export const getLockedColorIdsFromValueConfig = (
  valueConfig: any,
  settingParametersList: any[]
): string[] | null => {
  if (!valueConfig) return null;
  const colorEntry = resolveColorConfigEntry(
    valueConfig,
    settingParametersList
  );
  if (!colorEntry) return null;

  const ids: string[] = colorEntry?.valueIds ?? [];
  const selectValuesCount =
    colorEntry?.selectValuesCount ??
    (ids.length > 0 ? ids.length : null);

  const presetFromName = inferPresetTypeFromUpdateName(
    valueConfig?.updateName ?? ""
  );
  if (presetFromName && presetFromName !== "custom") {
    return ids.length > 0 ? ids : null;
  }

  if (isCountOnlyColorPreset(valueConfig)) {
    return null;
  }

  if (ids.length > 0 && selectValuesCount != null && ids.length === selectValuesCount) {
    return ids;
  }

  return null;
};

export const getMaxColorSelectionFromValueConfig = (
  valueConfig: any,
  settingParametersList: any[]
): number | null => {
  if (!valueConfig) return null;
  const colorEntry = resolveColorConfigEntry(
    valueConfig,
    settingParametersList
  );
  if (!colorEntry) {
    return parseColorCountFromUpdateName(valueConfig?.updateName ?? "");
  }
  return (
    colorEntry.selectValuesCount ??
    (colorEntry.valueIds?.length > 0 ? colorEntry.valueIds.length : null) ??
    parseColorCountFromUpdateName(valueConfig?.updateName ?? "")
  );
};

export const requiresManualColorSelection = (
  valueConfig: any,
  settingParametersList: any[]
): boolean => {
  if (!valueConfig) return false;
  const lockedIds = getLockedColorIdsFromValueConfig(
    valueConfig,
    settingParametersList
  );
  if (lockedIds?.length) return false;
  const requiredCount = getMaxColorSelectionFromValueConfig(
    valueConfig,
    settingParametersList
  );
  return requiredCount != null && requiredCount > 0;
};

export const validatePrintingColorSelection = (
  valueConfig: any,
  settingParametersList: any[],
  selectedColorIds: string[]
): PrintingColorValidationResult => {
  const lockedIds = getLockedColorIdsFromValueConfig(
    valueConfig,
    settingParametersList
  );
  if (lockedIds?.length) {
    return { valid: true };
  }

  const requiredCount = getMaxColorSelectionFromValueConfig(
    valueConfig,
    settingParametersList
  );
  const selectedCount = selectedColorIds?.filter(Boolean).length ?? 0;

  if (requiredCount == null) {
    if (selectedCount === 0) {
      return {
        valid: false,
        messageKey: "products.offsetPrice.admin.selectAtLeastOneColor",
      };
    }
    return { valid: true };
  }

  if (selectedCount !== requiredCount) {
    return {
      valid: false,
      messageKey: "products.offsetPrice.admin.selectExactColors",
      messageParams: { count: requiredCount },
    };
  }

  return { valid: true };
};
